import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Users, 
  Award, 
  Shield, 
  Zap,
  ArrowRight 
} from 'lucide-react';

const stats = [
  { number: '15+', label: 'Years Experience', icon: <Award className="h-6 w-6" /> },
  { number: '500+', label: 'Projects Completed', icon: <Zap className="h-6 w-6" /> },
  { number: '50+', label: 'Expert Team Members', icon: <Users className="h-6 w-6" /> },
  { number: '99.9%', label: 'Safety Record', icon: <Shield className="h-6 w-6" /> }
];

const capabilities = [
  'Fiber to the Home (FTTH)',
  'Fiber to the Business (FTTB)', 
  'Underground & Aerial Construction',
  'Emergency Response & Restoration',
  'Network Design & Engineering',
  'Project Management & Coordination',
  'Equipment Installation & Testing',
  'Maintenance & Support Services'
];

export default function AboutSection() {
  const handleGetQuote = () => {
    console.log('Get Quote clicked - scrolling to contact form');
    const contactElement = document.getElementById('contact');
    if (contactElement) {
      // Get the element's position relative to the document
      const rect = contactElement.getBoundingClientRect();
      const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
      const elementTop = rect.top + currentScroll;
      const headerHeight = 100; // Fine-tuned offset for optimal positioning
      const targetPosition = elementTop - headerHeight;
      
      console.log('Element top:', elementTop);
      console.log('Target scroll position:', targetPosition);
      
      window.scrollTo({
        top: targetPosition,
        behavior: 'smooth'
      });
    } else {
      console.log('Contact element not found!');
    }
  };

  return (
    <section className="py-16 bg-muted/30 scroll-mt-20" id="about">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-6">
                Leading Telecom Infrastructure 
                <span className="block text-primary">Since 2008</span>
              </h2>
              
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  Fiber Methods has been at the forefront of telecommunications infrastructure 
                  development, connecting communities across the nation with reliable, 
                  high-speed fiber networks.
                </p>
                <p>
                  Our experienced team of engineers, project managers, and field technicians 
                  delivers comprehensive solutions from initial design through ongoing maintenance, 
                  ensuring your infrastructure investment delivers maximum value and reliability.
                </p>
                <p>
                  With a proven track record of successful deployments and an unwavering 
                  commitment to safety and quality, we're the trusted partner for telecom 
                  companies, municipalities, and service providers nationwide.
                </p>
              </div>
            </div>

            {/* Capabilities */}
            <div>
              <h3 className="text-xl font-semibold text-foreground mb-4">Core Capabilities</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {capabilities.map((capability, index) => (
                  <Badge 
                    key={index} 
                    variant="secondary" 
                    className="px-3 py-2 text-sm justify-start whitespace-normal"
                    data-testid={`capability-${index}`}
                  >
                    <ArrowRight className="h-3 w-3 mr-2 text-primary flex-shrink-0" />
                    {capability}
                  </Badge>
                ))}
              </div>
            </div>

            {/* CTA */}
            <div className="pt-4">
              <Button 
                size="lg"
                onClick={handleGetQuote}
                data-testid="button-get-quote-about"
              >
                Get Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-6">
            {stats.map((stat, index) => (
              <Card 
                key={index} 
                className="text-center p-6 bg-background/50 border-muted hover:border-primary/20 transition-colors"
                data-testid={`stat-card-${index}`}
              >
                <CardContent className="p-0 space-y-3">
                  <div className="inline-flex p-3 bg-primary/10 rounded-full text-primary">
                    {stat.icon}
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-foreground" data-testid={`stat-number-${index}`}>
                      {stat.number}
                    </div>
                    <div className="text-sm text-muted-foreground" data-testid={`stat-label-${index}`}>
                      {stat.label}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Additional Section */}
        <div className="mt-16 text-center">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Committed to Excellence and Safety
            </h3>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Our team follows industry best practices and maintains the highest safety standards 
              on every project. From small residential installations to large-scale municipal 
              deployments, we deliver quality results on time and within budget.
            </p>
            
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <div className="flex items-center space-x-2 text-muted-foreground">
                <Shield className="h-4 w-4 text-primary" />
                <span>OSHA Certified</span>
              </div>
              <div className="flex items-center space-x-2 text-muted-foreground">
                <Award className="h-4 w-4 text-primary" />
                <span>Industry Certified</span>
              </div>
              <div className="flex items-center space-x-2 text-muted-foreground">
                <Users className="h-4 w-4 text-primary" />
                <span>Experienced Team</span>
              </div>
              <div className="flex items-center space-x-2 text-muted-foreground">
                <Zap className="h-4 w-4 text-primary" />
                <span>Proven Results</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}